import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { useCallback, useState } from "react";
import { Image, Platform, StatusBar } from "react-native";
import { useFragment } from "react-relay";

import { Menu } from "@whoosh/design-system/src/components/overlay/Menu";
import {
  Box,
  HStack,
  Text,
  VStack,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";
import { getFill } from "@whoosh/utilities/src/Cloudinary";

import { CurrentClubWeather } from "../../../../components/CurrentClubWeather";
import { MemberListModal } from "../../../MemberListModal";
import { FacilityLogo } from "../../../Session/components/FacilityLogo";
import { NoNav_atom } from "../../atoms";
import { useGetTabBarItems } from "../../hooks/useGetTabBarItems";
import { useShouldHideNav } from "../../hooks/useShouldHideNav";
import { BookButton } from "../BookButton";
import { TabBarMoreItem } from "../TabBarNav/components/TabBarMoreItem";
import { TabBarOverflowItem } from "../TabBarOverflowItem";
import { TopNavBarFragment_session$key } from "./__generated__/TopNavBarFragment_session.graphql";
import { TopNavBarItem } from "./components/TopNavBarItem";

const NAV_IMAGE_ID = "nav-image_b4yfml.png";

type TopNavBarProps = {
  sessionRef: TopNavBarFragment_session$key;
};
/** this is the desktop menu */
export const TopNavBar = ({ sessionRef }: TopNavBarProps) => {
  const session = useFragment(TopNavBarFragmentString, sessionRef);
  const club = session.currentClientProfile?.member?.club;

  const [noNav] = useAtom(NoNav_atom);

  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);

  const openMemberModal = useCallback(() => {
    setIsMemberModalOpen(true);
  }, []);

  const closeMemberModal = useCallback(() => {
    setIsMemberModalOpen(false);
  }, []);

  const { items, overflowItems } = useGetTabBarItems({
    openMemberModal,
    sessionRef: session,
  });

  const currentClubBG = club?.appCustomization?.backgroundImage?.publicId;

  const navImage = getFill(
    currentClubBG ? currentClubBG : NAV_IMAGE_ID,
    1400,
    933
  )?.toURL();

  const isMobileScreen = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: true,
    agendaSplit: true,
    eventCardColumn: true,
    lg: false,
    xl: false,
  });

  const hideNav = useShouldHideNav();

  const shouldRenderNull = isMobileScreen;

  return shouldRenderNull ? null : (
    <Box
      safeAreaTop
      backgroundColor="surface-primary"
      style={{ zIndex: 20 }}
      visible={!hideNav}
      testID="top-nav-bar-container"
      position="relative"
      webPosition={Platform.OS === "web" ? "sticky" : undefined}
      top={0}
    >
      <Box maxWidth={1240} width="100%" style={{ marginHorizontal: "auto" }}>
        <Box position="relative" width="100%">
          <Box
            overflow="hidden"
            style={{ borderBottomLeftRadius: 32, borderBottomRightRadius: 32 }}
            width="100%"
            position="relative"
          >
            <Box
              position="absolute"
              top={0}
              right={0}
              left={0}
              bottom={0}
              style={{ backgroundColor: "rgba(28, 39, 39, 0.75)", zIndex: 1 }}
            />

            <Image
              alt={t`Decorative Background`}
              source={{ uri: navImage }}
              resizeMode="cover"
              style={{ width: "100%", height: 152 }}
            />
          </Box>

          <Box
            backgroundColor="surface-primary"
            justifyContent={"center"}
            paddingLeft={6}
            paddingRight={7}
            borderRadius="6"
            position="absolute"
            bottom={-16}
            right={64}
            left={64}
            height={104}
            style={{
              shadowColor: "rgba(0, 0, 0, 0.08)",
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: 1,
              shadowRadius: 48,
              elevation: 12, // Adjust this value as needed for Android; it doesn't have an exact match with CSS box-shadow
            }}
          >
            {noNav ? null : (
              <HStack alignItems="center" backgroundColor={"surface-primary"}>
                <StatusBar
                  animated={true}
                  barStyle="light-content"
                  translucent
                  backgroundColor="transparent"
                />
                <HStack alignItems="center" gap={4} maxWidth={384}>
                  {club ? (
                    <FacilityLogo
                      imageProps={{
                        style: { width: 56, height: 56, borderRadius: 56 },
                      }}
                      clubRef={club}
                    />
                  ) : null}
                  <VStack gap={1}>
                    <Text variant="serifTitle3" color="grayscale-1000">
                      {session.currentClientProfile?.member?.club?.name}
                    </Text>
                    <CurrentClubWeather />
                  </VStack>
                </HStack>

                <HStack flex={1} gap={2} justifyContent="flex-end">
                  {items.map((item, index) => (
                    <TopNavBarItem item={item} key={index} />
                  ))}
                  {overflowItems.length ? (
                    <Menu
                      openPosition="bottom-end"
                      verticalOffset={16}
                      horizontalOffset={32}
                      trigger={({ onPress, isOpen }) => {
                        return (
                          <Pressable
                            onPress={onPress}
                            accessibilityRole="button"
                            aria-haspopup="menu"
                            accessibilityState={{ expanded: isOpen }}
                            style={{ borderRadius: 16 }}
                            _hover={{
                              backgroundColor: isOpen
                                ? "grayscale-500"
                                : "grayscale-200",
                            }}
                            backgroundColor={
                              isOpen ? "surface-secondary" : "grayscale-0"
                            }
                            _pressed={{
                              backgroundColor: "grayscale-500",
                            }}
                          >
                            <TabBarMoreItem />
                          </Pressable>
                        );
                      }}
                      content={({ closeMenu }) => (
                        <Menu.Content size="regular">
                          <VStack accessibilityRole="menu">
                            {overflowItems.map((item, i) =>
                              item.isDivider ? (
                                <Box
                                  key={i}
                                  height={1}
                                  bg="surface-dividers"
                                  marginVertical={1}
                                />
                              ) : (
                                <TabBarOverflowItem
                                  key={i}
                                  label={item.label}
                                  selected={item.selected}
                                  onPress={() => {
                                    item.routeHandler();
                                    closeMenu();
                                  }}
                                  icon={(props) => <item.icon {...props} />}
                                />
                              )
                            )}
                          </VStack>
                        </Menu.Content>
                      )}
                    />
                  ) : null}
                  <BookButton sessionRef={session} />
                </HStack>
              </HStack>
            )}
          </Box>
        </Box>
      </Box>
      <MemberListModal isOpen={isMemberModalOpen} onClose={closeMemberModal} />
    </Box>
  );
};

export const TopNavBarFragmentString = graphql`
  fragment TopNavBarFragment_session on Session {
    ...BookButton_session
    ...useGetTabBarItemsFragment_session
    currentClientProfile {
      member {
        club {
          ...FacilityLogoFragment_club
          id
          name
          appCustomization {
            backgroundImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
        }
      }
    }
  }
`;
